// IT & Facilities — Desk & Office Space Request (PDF page 18)
import React, { useEffect, useState } from 'react';
import { wsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Field, Input, Select, Flow, Tabs, ReqStatus, Empty, Loading, Spin, Badge,
  useAsync, useQueryParam, fmtDate, today, BUSINESS_UNITS,
} from '../../components/ui';
import RequestDetail from '../../components/RequestDetail';
import { notify } from '../../utils/notify';

const NEEDS = ['Power', 'Network', 'Monitor', 'Storage'];

export default function DeskRequest() {
  const { user } = useAuth();
  const openId = useQueryParam('id');
  const [f, setF] = useState({ office: '', floor: '', requestType: 'Permanent desk', team: user?.businessUnit || 'Water Business Unit', seats: 1, startDate: today(), endDate: '', needs: ['Power', 'Network', 'Monitor'], accessibility: '', reason: '' });
  const [desk, setDesk] = useState('');
  const [saving, setSaving] = useState(null);
  const [tab, setTab] = useState('mine');
  const [detail, setDetail] = useState(openId);
  const map = useAsync(() => wsAPI.desks({ office: f.office || undefined, floor: f.floor || undefined }), [f.office, f.floor]);
  const mine = useAsync(() => wsAPI.listRequests({ type: 'desk', scope: 'mine' }), []);
  const queue = useAsync(() => wsAPI.listRequests({ type: 'desk', scope: 'all' }), []);
  useEffect(() => { if (map.data && !f.office) setF((p) => ({ ...p, office: map.data.office, floor: String(map.data.floor) })); }, [map.data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (openId) setDetail(openId); }, [openId]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const offices = map.data?.offices || [];
  const floors = offices.find((o) => o.office === f.office)?.floors || [];
  const desks = map.data?.desks || [];

  const save = async (submit) => {
    if (submit && !f.reason.trim()) { notify.error('Enter the reason for request'); return; }
    if (submit && ['Permanent desk', 'Hot desk'].includes(f.requestType) && !desk) { notify.error('Select an available desk on the seating map'); return; }
    setSaving(submit ? 'submit' : 'draft');
    try {
      const r = await wsAPI.createRequest({
        type: 'desk', title: `${f.requestType}${desk ? ` — D-${desk}` : ''} · ${f.office} floor ${f.floor}`, location: f.office, businessUnit: f.team,
        requiredBy: f.startDate || undefined, data: { ...f, deskNo: desk || undefined, needs: f.needs.join(', ') }, submit,
      });
      notify.success(submit ? `${r.request.refNo} submitted — Manager review next` : `Draft saved — ${r.request.refNo}`);
      setDesk('');
      map.reload(); mine.reload(); queue.reload();
    } catch (e) { notify.error(e.message || 'Could not save'); } finally { setSaving(null); }
  };

  const stageFields = (r, sd, setSd) => (r.stages[r.currentStage]?.key === 'facilities' ? (
    <Field label="Assign desk" hint={`Requested: ${r.data?.deskNo ? `D-${r.data.deskNo}` : '—'}`}>
      <Select value={sd.assignedDesk ?? r.data?.deskNo ?? ''} onChange={(e) => setSd('assignedDesk', e.target.value)} placeholder="Select desk" options={desks.filter((d) => d.state === 'available' || d.desk === r.data?.deskNo).map((d) => ({ value: d.desk, label: `D-${d.desk}` }))} />
    </Field>
  ) : null);

  const cols = [0, 1, 2].map((c) => desks.filter((_, i) => Math.floor(i / 4) === c));
  const pendingForMe = (queue.data?.requests || []).filter((r) => r.canAct);
  const rows = tab === 'mine' ? (mine.data?.requests || []) : pendingForMe;

  return (
    <PageShell section="IT & Facilities">
      <PageHead title="Desk & Office Space Request" subtitle={`Form ID: TSE-ADM-FRM-DSK-${new Date().getFullYear()}-001-R0 (Proposed)`} />
      <div className="tse-split half mb-18" style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.05fr)' }}>
        <Card title={<span style={{ fontSize: '1.014rem' }}>Request Details</span>}>
          <div className="tse-form-grid c2">
            <Field label="Requestor"><Input value={user?.userName || ''} readOnly /></Field>
            <Field label="Employee ID"><Input value={user?.employeeCode || ''} readOnly /></Field>
            <Field label="Office"><Select value={f.office} onChange={(e) => { set('office', e.target.value); set('floor', ''); setDesk(''); }} options={offices.map((o) => o.office)} /></Field>
            <Field label="Floor"><Select value={f.floor} onChange={(e) => { set('floor', e.target.value); setDesk(''); }} options={floors.map(String)} /></Field>
            <Field label="Request type"><Select value={f.requestType} onChange={(e) => set('requestType', e.target.value)} options={['Permanent desk', 'Hot desk', 'Project room', 'Meeting room', 'Storage space']} /></Field>
            <Field label="Team"><Select value={f.team} onChange={(e) => set('team', e.target.value)} options={BUSINESS_UNITS} /></Field>
          </div>
          <div className="tse-form-grid c3" style={{ marginTop: 8 }}>
            <Field label="Seats required"><Select value={f.seats} onChange={(e) => set('seats', Number(e.target.value))} options={[1, 2, 3, 4, 5, 6].map(String)} /></Field>
            <Field label="Start date"><Input type="date" min={today()} value={f.startDate} onChange={(e) => set('startDate', e.target.value)} /></Field>
            <Field label="End date"><Select value={f.endDate ? 'date' : ''} onChange={(e) => set('endDate', e.target.value === 'date' ? today() : '')} options={[{ value: '', label: 'Not applicable' }, { value: 'date', label: 'Set end date' }]} />
              {f.endDate && <Input type="date" min={f.startDate} value={f.endDate} onChange={(e) => set('endDate', e.target.value)} style={{ marginTop: 6 }} />}
            </Field>
          </div>
          <div style={{ marginTop: 8 }}>
            <div className="tse-label" style={{ marginBottom: 8 }}>Needs (select all that apply)</div>
            <div className="tse-row" style={{ gap: 8 }}>{NEEDS.map((n) => (
              <label key={n} className="tse-check"><input type="checkbox" checked={f.needs.includes(n)} onChange={(e) => set('needs', e.target.checked ? [...f.needs, n] : f.needs.filter((x) => x !== n))} />{n}</label>
            ))}</div>
          </div>
          <Field label="Accessibility needs (optional)" style={{ marginTop: 8 }}><Input value={f.accessibility} onChange={(e) => set('accessibility', e.target.value)} placeholder="e.g. wheelchair access, adjustable desk, near lift (optional)" /></Field>
          <Field label="Reason for request" style={{ marginTop: 8 }}><Input value={f.reason} onChange={(e) => set('reason', e.target.value)} placeholder="e.g. Project coordination role." /></Field>
        </Card>

        <Card title={<span style={{ fontSize: '1.014rem' }}>Illustrative seating map</span>} actions={<span className="tse-muted">Floor {f.floor} – {f.office} Office</span>}>
          <div className="tse-legend" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
            <span><i style={{ background: '#5ccfb2' }} />Available</span><span><i style={{ background: '#c3cad1' }} />Occupied</span><span><i style={{ background: '#f5d38c' }} />Pending</span><span><i style={{ background: '#1a84d8' }} />Selected</span>
          </div>
          {map.loading ? <Loading /> : (
            <div className="tse-floor">
              <div className="desks">
                {cols.map((col, ci) => (
                  <div key={ci} className="col">
                    {col.map((d) => (
                      <button key={d.desk} type="button" className={`tse-desk ${desk === d.desk ? 'selected' : d.state}`} disabled={d.state !== 'available'} title={d.by ? `${d.by} (${d.refNo || ''})` : d.state}
                        onClick={() => setDesk(desk === d.desk ? '' : d.desk)}>{d.desk}</button>
                    ))}
                  </div>
                ))}
              </div>
              <div className="conf"><i className="bi bi-easel" style={{ fontSize: '1.872rem' }} />Conference<br />Room</div>
              <div className="aisle">- - - - - - - - - - &nbsp; Main Aisle &nbsp; - - - - - - - - - -</div>
            </div>
          )}
          <div className="tse-note info" style={{ marginTop: 8 }}>
            <i className="bi bi-circle-fill" style={{ color: '#1a84d8' }} />
            <div>{desk ? <><strong>Selected desk: D-{desk}</strong> &nbsp;•&nbsp; Pending facilities confirmation.</> : 'Select an available (green) desk on the map.'}</div>
          </div>
        </Card>
      </div>

      <div className="tse-card mb-18"><div className="tse-card-body tse-row" style={{ gap: 8 }}>
        <div className="tse-strong" style={{ fontSize: '0.989rem' }}>Request Workflow</div>
        <div style={{ flex: 1, minWidth: 420 }}>
          <Flow small plainArrows nodes={[
            { icon: 'bi-person', label: 'Manager', sub: 'Review & approve' },
            { icon: 'bi-gear', label: 'Admin / Facilities', sub: 'Assign desk & confirm' },
            { icon: 'bi-laptop', label: 'IT setup', sub: 'Setup equipment' },
            { icon: 'bi-check-circle', label: 'Handover', sub: 'Complete & close' },
          ]} />
        </div>
        <div style={{ borderLeft: '1px solid #e1e9f1', paddingLeft: 17 }} className="tse-row">
          <button type="button" className="tse-btn lg outline" style={{ minWidth: 150 }} onClick={() => save(false)} disabled={!!saving}>{saving === 'draft' ? <Spin /> : null}Save draft</button>
          <button type="button" className="tse-btn lg" style={{ minWidth: 170 }} onClick={() => save(true)} disabled={!!saving}>{saving === 'submit' ? <Spin /> : null}Submit request</button>
        </div>
      </div></div>

      <Card bodyClass="flush" title="Desk requests" actions={<Tabs value={tab} onChange={setTab} tabs={[{ key: 'mine', label: 'My requests' }, ...(['admin', 'manager', 'hod'].includes(user?.role) ? [{ key: 'queue', label: 'Awaiting my action', count: pendingForMe.length }] : [])]} />}>
        {mine.loading ? <Loading /> : !rows.length ? <Empty icon="bi-building" title="No desk requests" /> : (
          <div className="tse-table-wrap"><table className="tse-table">
            <thead><tr><th>Request</th><th>Details</th><th>Requester</th><th>Desk</th><th>Start</th><th>Status</th><th /></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r._id} className="clickable" onClick={() => setDetail(r._id)}>
                <td className="strong">{r.refNo}</td><td>{r.title}</td><td>{r.requesterName}</td>
                <td>{r.data?.assignedDesk || r.data?.deskNo ? <Badge tone="blue">D-{r.data.assignedDesk || r.data.deskNo}</Badge> : '—'}</td><td>{fmtDate(r.requiredBy)}</td>
                <td><ReqStatus request={r} /></td><td><button type="button" className="tse-btn xs outline">{r.canAct ? 'Review' : 'Details'}</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </Card>
      <RequestDetail id={detail} onClose={() => setDetail(null)} onChanged={() => { mine.reload(); queue.reload(); map.reload(); }} stageFields={stageFields} />
    </PageShell>
  );
}
